import axios from 'axios';
import { TextKey, currentLang, translate } from '../../../i18n';
import { companyLocale, formatCurrency as companyMoney } from '../../../utils/format/currency';

/** Texto en el idioma de la persona, para funciones fuera de componentes. */
const tr = (key: TextKey, vars?: Record<string, string | number>) => translate(currentLang(), key, vars);

/** Rutas de menú del módulo Inventario (deben coincidir con el seeder de navegación del API). */
export const INVENTORY_ROUTES = {
  products: '/inventory/products',
  incomes: '/inventory/receipts',
  transfers: '/inventory/transfers',
  writeOffs: '/inventory/write-offs',
  returns: '/inventory/returns',
  stock: '/inventory/stock',
} as const;

export type StockStatus = 'DISPONIBLE' | 'NO_PRESTABLE';

/** Etiquetas de estado; se leen en el idioma actual cada vez que se consultan. */
export const STOCK_STATUS_LABEL: Record<StockStatus, string> = {
  get DISPONIBLE() {
    return tr('inventory.status.available');
  },
  get NO_PRESTABLE() {
    return tr('inventory.status.notLendable');
  },
};

export interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

export interface OfficeOption {
  id: number;
  name: string;
}

export interface RouteOption {
  id: number;
  name: string;
}

/** Ubicación de inventario: oficina O ruta. */
export interface InventoryLocation {
  officeId?: number | null;
  routeId?: number | null;
}

export interface Product {
  id: number;
  title: string;
  description?: string;
  sku?: string | null;
  unit?: string | null;
  price: number;
  /** Ya no se usa desde 1.7.0 (el precio incluye la utilidad); siempre llega null. */
  marginPercent?: number | string | null;
  costPrice?: number | string | null;
  hasExpiration?: boolean;
  shelfLifeDays?: number | null;
  minStock?: number | null;
  status?: { id: number; name: string } | null;
  active?: boolean;
  /** Oficina dueña del producto (null = producto antiguo sin oficina; solo lo ve el superadmin). */
  officeId?: number | null;
  office?: { id: number; name: string } | null;
  /** Resumen para prestar en las oficinas/rutas del usuario (lo calcula el API). */
  readiness?: ProductReadiness;
}

export interface ProductReadiness {
  status: 'READY' | 'NO_STOCK_IN_COLLECTS' | 'NOT_ASSIGNED';
  assigned: number;
  routesWithStock: number;
  officeStock: number;
}

/** Texto y color del estado "¿se puede prestar?" de un producto en el listado. */
export const readinessLabel = (r?: ProductReadiness): { label: string; color: 'success' | 'warning' | 'default'; hint: string } => {
  if (!r || r.status === 'NOT_ASSIGNED') {
    return { label: tr('inventory.readiness.noOffice'), color: 'default', hint: tr('inventory.readiness.noOfficeHint') };
  }
  if (r.status === 'NO_STOCK_IN_COLLECTS') {
    return {
      label: r.officeStock > 0 ? tr('inventory.readiness.pendingSend') : tr('inventory.readiness.noStock'),
      color: 'warning',
      hint: r.officeStock > 0
        ? tr('inventory.readiness.pendingSendHint', { n: r.officeStock })
        : tr('inventory.readiness.noStockHint'),
    };
  }
  return {
    label: tr(r.routesWithStock === 1 ? 'inventory.readiness.readyOne' : 'inventory.readiness.readyMany', { n: r.routesWithStock }),
    color: 'success',
    hint: tr('inventory.readiness.readyHint'),
  };
};

export interface CatalogItem {
  productId: number;
  title: string;
  sku?: string | null;
  unit?: string | null;
  hasExpiration?: boolean;
  catalogPrice: number;
  price: number;
  minStock: number | null;
  enabled: boolean;
  productActive: boolean;
  source: 'office' | 'collect' | null;
  /** Aún no se presta en la ubicación; un ingreso/traslado lo habilita automáticamente. */
  willEnable?: boolean;
}

export interface StockRow {
  productId: number;
  title: string;
  sku?: string | null;
  unit?: string | null;
  price: number;
  minStock: number | null;
  available: number;
  notLendable: number;
  enabled?: boolean;
}

export interface BatchRow {
  productId: number;
  title: string;
  batchId: number;
  lotCode?: string | null;
  expirationDate?: string | null;
  unitCost: number;
  status: StockStatus;
  quantity: number;
}

export interface NamedRef {
  id: number;
  name: string;
}

export interface WriteOffReason {
  id: number;
  name: string;
  code?: string | null;
  requiresExpiration?: boolean;
}

/** Claves de texto para los `code` que devuelve el API de inventario. */
const ERROR_MESSAGES: Record<string, TextKey> = {
  INSUFFICIENT_STOCK: 'inventory.errors.INSUFFICIENT_STOCK',
  PRODUCT_NOT_ASSIGNED: 'inventory.errors.PRODUCT_NOT_ASSIGNED',
  PRODUCT_OTHER_OFFICE: 'inventory.errors.PRODUCT_OTHER_OFFICE',
  OFFICE_REQUIRED: 'inventory.errors.OFFICE_REQUIRED',
  PRODUCT_OFFICE_LOCKED: 'inventory.errors.PRODUCT_OFFICE_LOCKED',
  PRODUCT_DISABLED_HERE: 'inventory.errors.PRODUCT_DISABLED_HERE',
  PRODUCT_LEND_NOT_ALLOWED: 'inventory.errors.PRODUCT_LEND_NOT_ALLOWED',
  CROSS_OFFICE_TRANSFER: 'inventory.errors.CROSS_OFFICE_TRANSFER',
  LOCATION_FORBIDDEN: 'inventory.errors.LOCATION_FORBIDDEN',
  EXPIRATION_REQUIRED: 'inventory.errors.EXPIRATION_REQUIRED',
  REASON_NOT_APPLICABLE: 'inventory.errors.REASON_NOT_APPLICABLE',
  RETURN_EXCEEDS_DELIVERED: 'inventory.errors.RETURN_EXCEEDS_DELIVERED',
  RETURN_EXCEEDS_PAID: 'inventory.errors.RETURN_EXCEEDS_PAID',
  RETURN_DISCOUNT_NOT_ALLOWED: 'inventory.errors.RETURN_DISCOUNT_NOT_ALLOWED',
  DUPLICATE_SKU: 'inventory.errors.DUPLICATE_SKU',
  BUSINESS_MODEL_REQUIRED: 'inventory.errors.BUSINESS_MODEL_REQUIRED',
  INVALID_UTILITY: 'inventory.errors.INVALID_UTILITY',
  INVALID_PRICING_MODE: 'inventory.errors.INVALID_PRICING_MODE',
  PRICING_MODE_NOT_ALLOWED: 'inventory.errors.PRICING_MODE_NOT_ALLOWED',
  BUSINESS_MODEL_NOT_AVAILABLE: 'inventory.errors.BUSINESS_MODEL_NOT_AVAILABLE',
  CASH_LEND_NOT_ALLOWED: 'inventory.errors.CASH_LEND_NOT_ALLOWED',
  PRODUCT_INACTIVE: 'inventory.errors.PRODUCT_INACTIVE',
  PRODUCT_WITHOUT_PRICE: 'inventory.errors.PRODUCT_WITHOUT_PRICE',
  CONSTANT_NOT_FOUND: 'inventory.errors.CONSTANT_NOT_FOUND',
  INVALID_CASH_VALUE: 'inventory.errors.INVALID_CASH_VALUE',
  BUSINESS_MODEL_CHANGE_BLOCKED: 'inventory.errors.BUSINESS_MODEL_CHANGE_BLOCKED',
};

export const apiErrorMessage = (error: any, fallback: string) => {
  const data = error?.response?.data;
  const code = data?.code as string | undefined;
  if (code && ERROR_MESSAGES[code]) {
    if (code === 'INSUFFICIENT_STOCK' && data?.available !== undefined) {
      return `${tr(ERROR_MESSAGES[code])} ${tr('inventory.errors.availableSuffix', { n: data.available })}`;
    }
    return tr(ERROR_MESSAGES[code]);
  }
  return data?.message || fallback;
};

/** Clave de idempotencia para POSTs (evita duplicados si se reintenta). */
export const newRequestId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/** Oficinas del usuario que prestan productos (objeto real): las únicas que se muestran en Inventario. */
export const fetchOffices = async (): Promise<OfficeOption[]> => {
  const { data } = await axios.get('offices');
  const list = Array.isArray(data?.data) ? data.data : [];
  return list
    .filter((o: any) => o?.businessModel?.allowProductLoans === true)
    .map((o: any) => ({ id: Number(o.id), name: String(o.name ?? '') }))
    .sort((a: OfficeOption, b: OfficeOption) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
};

export const fetchRoutes = async (officeId: number): Promise<RouteOption[]> => {
  if (!officeId) return [];
  const { data } = await axios.get(`offices/${officeId}/routes`);
  const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  return list
    .map((c: any) => ({ id: Number(c.id), name: String(c.name ?? '') }))
    .sort((a: RouteOption, b: RouteOption) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
};

export const locationParams = (location: InventoryLocation) =>
  location.routeId ? { routeId: location.routeId } : { officeId: location.officeId };

export const fetchCatalog = async (location: InventoryLocation, options: { includeUnassigned?: boolean } = {}): Promise<CatalogItem[]> => {
  const { data } = await axios.get('product-catalog', {
    params: { ...locationParams(location), ...(options.includeUnassigned ? { includeUnassigned: 1 } : {}) },
  });
  return Array.isArray(data?.data) ? data.data : [];
};

export type ReadinessStatus = 'READY' | 'NEEDS_TRANSFER' | 'NO_STOCK' | 'NOT_ENABLED' | 'DISABLED' | 'INACTIVE';

export interface ReadinessRow {
  productId: number;
  title: string;
  sku?: string | null;
  status: ReadinessStatus;
  enabled: boolean;
  source: 'office' | 'collect' | null;
  price: number;
  marginPercent: number | string | null;
  officeAvailable: number;
  routeAvailable: number | null;
  officeAssignmentId: number | null;
  routeAssignmentId: number | null;
}

export interface ReadinessResponse {
  officeId: number;
  routeId: number | null;
  officeName: string | null;
  routeName: string | null;
  allowProductLoans: boolean;
  pricingMode: 'CONSTANT' | 'PRODUCT' | null;
  summary: { ready: number; needsTransfer: number; noStock: number; notEnabled: number };
  data: ReadinessRow[];
}

export const fetchReadiness = async (location: InventoryLocation): Promise<ReadinessResponse> => {
  const { data } = await axios.get('inventory/readiness', { params: locationParams(location) });
  return data;
};

export const fetchStock = async (location: InventoryLocation, withBatches = false) => {
  const { data } = await axios.get('inventory', {
    params: { ...locationParams(location), ...(withBatches ? { detail: 'batches' } : {}) },
  });
  return {
    data: (Array.isArray(data?.data) ? data.data : []) as StockRow[],
    batches: (Array.isArray(data?.batches) ? data.batches : []) as BatchRow[],
  };
};

export const formatMoney = (value: unknown) => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? companyMoney(n) : '';
};

export const formatDate = (value: unknown) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

export const locationLabel = (row: { office?: NamedRef | null; route?: NamedRef | null }) =>
  row.route
    ? tr('inventory.location.route', { name: row.route.name })
    : row.office
      ? tr('inventory.location.office', { name: row.office.name })
      : '';

/**
 * Precios (1.7.0): el precio ES el valor del préstamo y la utilidad es informativa, sobre el costo.
 *   costo 10.000 · precio 12.000 → utilidad $2.000 · 20 %
 */
export const utilityPercentOf = (cost: number, price: number): number | null => {
  if (!(cost > 0) || !(price > 0)) return null;
  return Math.round((price / cost - 1) * 100 * 100) / 100;
};
export const priceFromUtilityPercent = (cost: number, pct: number): number | null =>
  cost > 0 && Number.isFinite(pct) ? Math.round(cost * (1 + pct / 100)) : null;
export const priceFromUtilityAmount = (cost: number, amount: number): number | null =>
  cost > 0 && Number.isFinite(amount) ? Math.round(cost + amount) : null;
/** Texto corto de la utilidad: "20 % · $ 2.000", o "—" sin costo. */
export const utilityLabel = (cost: number | null | undefined, price: number | null | undefined): string => {
  const c = Number(cost || 0);
  const p = Number(price || 0);
  const pct = utilityPercentOf(c, p);
  if (pct === null) return '—';
  return `${pct.toLocaleString(companyLocale())} % · ${formatMoney(p - c)}`;
};
